
import { Link } from 'react-router-dom';

export default function FamilyPharmacist() {
  const services = [
    {
      title: '服薬履歴管理',
      description: '全ての処方薬を一元管理し、重複投薬や相互作用をチェック',
      icon: 'ri-file-list-3-line',
      features: ['処方薬の一元管理', '服薬状況の記録', '副作用モニタリング', '薬歴の継続管理']
    },
    {
      title: '24時間相談対応',
      description: '緊急時や夜間でも薬に関する相談に対応',
      icon: 'ri-24-hours-line',
      features: ['夜間・休日対応', '緊急時相談', '電話・オンライン対応', '迅速な問題解決']
    },
    {
      title: '在宅訪問サービス',
      description: 'ご自宅での服薬指導や薬の管理をサポート',
      icon: 'ri-home-heart-line',
      features: ['自宅での服薬指導', '薬の整理・管理', '介護者への指導', '定期的な訪問']
    },
    {
      title: '健康管理サポート',
      description: '薬物療法を通じた総合的な健康管理',
      icon: 'ri-heart-pulse-line',
      features: ['血圧・血糖値管理', '生活習慣指導', '健康相談', '医師との連携']
    }
  ];

  const benefits = [
    {
      title: '安心・安全な薬物療法',
      description: '専門薬剤師による継続的な管理で、安全で効果的な薬物療法を実現',
      icon: 'ri-shield-check-line',
      color: 'bg-green-50 text-green-600'
    },
    {
      title: '個別最適化された服薬指導',
      description: '患者さん一人ひとりの状況に合わせたオーダーメイドの指導',
      icon: 'ri-user-settings-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: '医療費の適正化',
      description: '重複投薬の防止や適切な薬剤選択により医療費を削減',
      icon: 'ri-money-dollar-circle-line',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: '生活の質の向上',
      description: '適切な薬物療法により、日常生活の質を向上',
      icon: 'ri-heart-line',
      color: 'bg-red-50 text-red-600'
    }
  ];

  const process = [
    {
      step: '1',
      title: '初回面談',
      description: '現在の服薬状況や健康状態について詳しくお聞きします',
      icon: 'ri-chat-3-line'
    },
    {
      step: '2',
      title: '薬歴の整理',
      description: '過去の処方薬や副作用歴を整理し、データベース化します',
      icon: 'ri-file-chart-line'
    },
    {
      step: '3',
      title: '服薬計画の作成',
      description: '個人に最適化された服薬スケジュールを作成します',
      icon: 'ri-calendar-check-line'
    },
    {
      step: '4',
      title: '継続的なフォロー',
      description: '定期的な面談や相談を通じて、継続的にサポートします',
      icon: 'ri-refresh-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-blue-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/f7d7dfe0feb864f2f0f2bbce26b53848.jpg"
            alt="かかりつけ薬剤師"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            かかりつけ<span className="text-teal-600">薬剤師</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            あなたとご家族の健康を継続的にサポートする、信頼できるパートナーとしてお役に立ちます
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">かかりつけ薬剤師制度</h2>
            <p className="text-xl text-gray-600">専門薬剤師による包括的な薬物療法管理</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">かかりつけ薬剤師のメリット</h2>
            <p className="text-xl text-gray-600">継続的な関係だからこそ実現できる価値</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6 text-center">
                <div className={`w-16 h-16 rounded-xl ${benefit.color} flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${benefit.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">サービス開始までの流れ</h2>
            <p className="text-xl text-gray-600">簡単な手続きで、すぐにサービスを開始できます</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold">{item.step}</span>
                </div>
                <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">かかりつけ薬剤師になるための要件</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">薬剤師の要件</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span>薬剤師認定制度認証機構が認証した研修認定制度等の研修を修了</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span>薬剤師として3年以上の薬局勤務経験</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span>週32時間以上の勤務</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">薬局の要件</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span>24時間開局または24時間調剤応需体制</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span>在宅患者訪問薬剤管理指導の実施</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span>かかりつけ薬剤師指導料の届出</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            あなたの健康パートナーとして
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            かかりつけ薬剤師として、あなたとご家族の健康を継続的にサポートいたします
          </p>
          <button 
            onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
            className="bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            相談を始める
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康サポート</Link></li>
                <li><Link to="/self-care" className="hover:text-white cursor-pointer">セルフケア</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
